import React, { useState, useMemo, useEffect } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';

import apiPublicRequest from '@/apiPublicRequest';
import ClientFormLinkComponent from '@/components/ClientsPublicPage/ClientFormLink/ClientFormLinkComponent';
import { parseInputName } from '@/helpers';
import { validateDocument } from '@/helpers/documentValidation';
import { masksByCountry } from '@/helpers/mask';
import { decodeToken } from '@/helpers/tokenGenerator';
import AddressService from '@/services/addressService';
import { showToast } from '@/store/ducks/toasts';

const classifications = [
  'IMOBILIARIA',
  'CORRETOR_DE_IMOVEIS',
  'PROPRIETARIO',
  'CONDOMINIO',
  'ADMINISTRADORA_DE_CONDOMINIOS',
  'SINDICO_PROFISSIONAL',
  'CONSTRUTORA_INCORPORADORA',
  'EMPREITEIRA',
  'INSTITUICAO_FINANCEIRA',
  'REDE_DE_VAREJO',
  'PESSOA_FISICA',
  'OUTROS',
];

const ClientFormLinkContainer = () => {
  const [busy, setBusy] = useState(false);
  const [type, setType] = useState('pj');
  const [privacyPolicy, setPrivacyPolicy] = useState(false);
  const [receiveInformation, setReceiveInformation] = useState(false);
  const [client, setClient] = useState({ client_type: 'pj' });
  const [documentError, setDocumentError] = useState('');

  const [states, setStates] = useState([]);
  const [cities, setCities] = useState([]);
  const [neighborhoods, setNeighborhoods] = useState([]);

  const dispatch = useDispatch();
  const { hash } = useParams();
  const navigate = useNavigate();
  const intl = useIntl();

  const franchise = useMemo(() => decodeToken(hash), [hash]);
  const franchiseeCountry = franchise?.country || 'BR';

  const isBrazilianFranchisee = franchiseeCountry === 'BR';
  const useTextInputForAddress = ['UY', 'PY', 'MX', 'CO'].includes(franchiseeCountry);

  const addressService = new AddressService(franchiseeCountry);

  const zipcodeMask = masksByCountry[franchiseeCountry].ZIPCODE_MASK;
  const cnpjMask = masksByCountry[franchiseeCountry].CNPJ_MASK;
  const cpfOnlyMask = masksByCountry[franchiseeCountry].CPF_ONLY_MASK;
  const phoneMask = masksByCountry[franchiseeCountry].PHONE_MASK;
  const cellphoneMask = masksByCountry[franchiseeCountry].CELLPHONE_MASK;
  const cellphoneMaskLength = masksByCountry[franchiseeCountry].CELLPHONE_MASK_LENGTH;

  useEffect(() => {
    setStates(addressService.getStates());
  }, []);

  const fillNeighborhoods = async cityName => {
    try {
      // First, try to find the city ID by name from the existing cities list
      const cityObj = cities.find(c => c.name === cityName);
      if (!cityObj) {
        // If it's a custom city name, clear neighborhoods list but preserve existing values
        setNeighborhoods([]);
        return [];
      }

      // Get neighborhoods for the existing city
      const data = await addressService.getNeighborhoodsByCity(cityObj.id);

      // Only clear neighborhood selection if we successfully loaded new neighborhoods
      if (data && data.length > 0) {
        setClient(state => ({
          ...state,
          address: {
            ...state.address,
            neighborhood: null,
            neighborhood_id: null,
          },
        }));
      }

      setNeighborhoods(data || []);
      return data || [];
    } catch (err) {
      console.debug('[DEBUG] fillNeighborhoods error ', err);
      setNeighborhoods([]);
      return [];
    }
  };

  const fillCities = async stateId => {
    // Don't make API call if state is empty, null, or undefined
    if (!stateId || stateId === '') {
      // Clear the cities and neighborhoods lists but preserve existing values
      setCities([]);
      setNeighborhoods([]);
      return [];
    }

    try {
      const data = await addressService.getCitiesByState(stateId);

      // Only clear city selection if we successfully loaded new cities
      if (data && data.length > 0) {
        setClient(oldState => ({
          ...oldState,
          address: {
            ...oldState.address,
            city: null,
            city_id: null,
          },
        }));
      }

      setCities(data || []);
      setNeighborhoods([]); // Clear neighborhoods when state changes
      return data || [];
    } catch (err) {
      console.debug('[DEBUG] fillCities error ', err);
      return [];
    }
  };

  const fillAddress = async (parent, zipcode) => {
    try {
      const address = await addressService.getLocationByZipcode(zipcode);

      if (address) {
        // Only update if we got valid address data
        await fillCities(address?.state_id);
        await fillNeighborhoods(address?.city);

        setClient(prevClient => ({
          ...prevClient,
          [parent]: {
            ...prevClient[parent],
            street: address?.street || prevClient[parent]?.street || '',
            neighborhood: address?.neighborhood || prevClient[parent]?.neighborhood || '',
            neighborhood_id: address?.neighborhood_id || prevClient[parent]?.neighborhood_id || '',
            city: address?.city || prevClient[parent]?.city || '',
            city_id: address?.city_id || prevClient[parent]?.city_id || '',
            state: address?.state || prevClient[parent]?.state || '',
            state_id: address?.state_id || prevClient[parent]?.state_id || '',
            zipcode,
          },
        }));
      } else {
        // If no address found, just update the zipcode and preserve existing values
        setClient(prevClient => ({
          ...prevClient,
          [parent]: {
            ...prevClient[parent],
            zipcode,
          },
        }));
      }
    } catch (err) {
      console.debug('[DEBUG] fillAddress error ', err);
      // On error, just update the zipcode and preserve existing values
      setClient(prevClient => ({
        ...prevClient,
        [parent]: {
          ...prevClient[parent],
          zipcode,
        },
      }));
    }
  };

  const validateDocumentField = (document, clientType = type) => {
    console.log('document', document);

    const isValid = validateDocument(document, clientType, franchiseeCountry);

    if (!isValid) {
      if (franchiseeCountry === 'BR') {
        setDocumentError(
          intl.formatMessage({
            id: clientType === 'pf' ? 'CLIENT_LINK.INVALID_CPF' : 'CLIENT_LINK.INVALID_CNPJ',
          })
        );
      } else {
        setDocumentError(intl.formatMessage({ id: 'CLIENT_LINK.INVALID_DOCUMENT' }));
      }
      return false;
    }

    setDocumentError('');
    return true;
  };

  const handleChange = async evt => {
    const { name } = evt.target;
    let { value } = evt.target;

    // Handle document field changes with validation
    if (name === 'document') {
      const normalizedValue = isBrazilianFranchisee && typeof value === 'string' ? value.toUpperCase() : value;

      setClient(prevClient => ({
        ...prevClient,
        document: normalizedValue,
      }));

      // Only validate when document has reached its full expected length
      const expectedLength = type === 'pf' ? 14 : 18; // CPF: 14, CNPJ: 18 (formatted)
      if (normalizedValue.length >= expectedLength) {
        validateDocumentField(normalizedValue);
      } else {
        setDocumentError('');
      }
      return;
    }

    const parsed = parseInputName(name, value);
    const parent = Object.keys(parsed)[0];

    if (name === 'privacy_policy') {
      setPrivacyPolicy(!privacyPolicy);
    }

    if (name === 'receive_information') {
      setReceiveInformation(!receiveInformation);
    }

    if (name === 'client_type') {
      setType(value);

      // Clear document validation when client type changes
      setDocumentError('');

      // If there's already a document entered, revalidate with new type
      if (client.document) {
        setTimeout(() => validateDocumentField(client.document, value), 0);
      }
    }

    if (name.includes('zipcode') && addressService.validateZipcode(value)) {
      await fillAddress(parent, value);
      return;
    }

    if (name.includes('state_id')) {
      // Update state immediately
      value = isBrazilianFranchisee ? Number(value) : value;
      parsed[parent].state_id = value;
      parsed[parent].state = states.find(state => state?.id === value)?.name;

      // Update client state immediately
      const parsedValue =
        typeof parsed[parent] !== 'string' ? { ...client[parent], ...parsed[parent] } : parsed[parent];
      setClient({ ...client, [parent]: parsedValue });

      // Load cities asynchronously without blocking the UI
      fillCities(value);
      return;
    }

    if (name.includes('city]')) {
      // City name changed - always store the name immediately
      parsed[parent].city = value;

      // Update client state immediately
      const parsedValue =
        typeof parsed[parent] !== 'string' ? { ...client[parent], ...parsed[parent] } : parsed[parent];
      setClient({ ...client, [parent]: parsedValue });

      // Find city_id dynamically to load neighborhoods
      const foundCity = cities.find(city => city.name === value);

      if (foundCity) {
        // Existing city - update city_id and load neighborhoods
        setClient(prevClient => ({
          ...prevClient,
          [parent]: {
            ...prevClient[parent],
            city_id: foundCity.id,
          },
        }));
        fillNeighborhoods(value);
      } else {
        // Custom city - clear city_id and neighborhoods
        setClient(prevClient => ({
          ...prevClient,
          [parent]: {
            ...prevClient[parent],
            city_id: '',
            neighborhood: '',
            neighborhood_id: '',
          },
        }));
        setNeighborhoods([]);
      }
      return;
    }

    if (name.includes('neighborhood]')) {
      // Neighborhood name changed - always store the name immediately
      parsed[parent].neighborhood = value;

      // Update client state immediately
      const parsedValue =
        typeof parsed[parent] !== 'string' ? { ...client[parent], ...parsed[parent] } : parsed[parent];
      setClient({ ...client, [parent]: parsedValue });

      // Find neighborhood_id dynamically
      const foundNeighborhood = neighborhoods.find(neighborhood => neighborhood.name === value);

      if (foundNeighborhood) {
        // Existing neighborhood - update neighborhood_id
        setClient(prevClient => ({
          ...prevClient,
          [parent]: {
            ...prevClient[parent],
            neighborhood_id: foundNeighborhood.id,
          },
        }));
      } else {
        // Custom neighborhood - clear neighborhood_id
        setClient(prevClient => ({
          ...prevClient,
          [parent]: {
            ...prevClient[parent],
            neighborhood_id: '',
          },
        }));
      }
      return;
    }

    const parsedValue = typeof parsed[parent] !== 'string' ? { ...client[parent], ...parsed[parent] } : parsed[parent];
    setClient({ ...client, [parent]: parsedValue });
  };

  const handleSubmit = async evt => {
    evt.preventDefault();

    try {
      setBusy(true);

      // Validate document before submission
      const isDocumentValid = validateDocumentField(client.document);

      if (!isDocumentValid) {
        dispatch(
          showToast({
            type: 'error',
            title: intl.formatMessage({ id: 'ERROR' }),
            text: intl.formatMessage({ id: 'CLIENT_LINK.INVALID_DOCUMENT' }),
            duration: 5000,
          })
        );
        setBusy(false);
        return;
      }

      const payload = {
        ...client,
        privacy_policy: privacyPolicy,
        receive_information: receiveInformation,
      };

      await apiPublicRequest.post(`/public/franchisees/${franchise.franchisee_id}/clients`, payload);

      navigate(`/new-client/${hash}/success`, { replace: true });
    } catch (err) {
      console.debug('[DEBUG] handleSubmit error ', err);

      if (err?.response?.data?.message) {
        dispatch(
          showToast({
            type: 'error',
            title: intl.formatMessage({ id: 'ERROR' }),
            text: intl.formatMessage({ id: err.response.data.message }),
            duration: 6000,
          })
        );
      } else {
        dispatch(
          showToast({
            type: 'error',
            title: intl.formatMessage({ id: 'ERROR' }),
            text: intl.formatMessage({ id: 'CLIENT_LINK.SAVE_ERROR' }),
            duration: 6000,
          })
        );
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <ClientFormLinkComponent
      busy={busy}
      client={client}
      classifications={classifications}
      states={states}
      cities={cities}
      neighborhoods={neighborhoods}
      privacyPolicy={privacyPolicy}
      receiveInformation={receiveInformation}
      type={type}
      onChange={handleChange}
      onSubmit={handleSubmit}
      zipcodeMask={zipcodeMask}
      cnpjMask={cnpjMask}
      cpfOnlyMask={cpfOnlyMask}
      phoneMask={phoneMask}
      cellphoneMask={cellphoneMask}
      cellphoneMaskLength={cellphoneMaskLength}
      documentError={documentError}
      useTextInputForAddress={useTextInputForAddress}
      country={franchiseeCountry}
    />
  );
};

export default ClientFormLinkContainer;
